/* eslint-disable import/first */
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import classnames from 'classnames';
import _trim from 'lodash/trim';
import _get from 'lodash/get';
import _merge from 'lodash/merge';
import PropTypes from 'prop-types';
// Date util library
import MomentUtils from '@date-io/moment';
// Material UI
import CssBaseline from '@material-ui/core/CssBaseline';
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
// HOCS
import withPermissions from 'hocs/WithPermissions';
// Models
import { queGetSlugByDomain } from 'models/cpm/slug';
import mProduct from 'models/product';
// Custom
import DialogsManager from 'components/dialogs/DialogsManager';
import ErrorBoundary from 'components/error/ErrorBoundary';
import { cpmGetSlugByDomain } from 'actions';
import LoadingDisplayer from 'components/misc/LoadingDisplayer';
import { cpmCurrentSlugShape } from 'constants/PropTypesShapes';
import Error404 from 'error404/components/ErrorManager';
import ResponseInterceptor from './ResponseInterceptor';
// = styles =
// 3rd
import 'styles/bootstrap.scss';
// custom
import 'styles/layout.scss';
import 'styles/ui.scss';
import 'styles/app.scss';
// themes
import main from './themes/main';
import themeOpac from './themes/opac';
import themeCpmClient from './themes/cpmClient';
import themeCpmManager from './themes/cpmManager';
// app
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

const { hostname: domainFromURL, pathname } = window.location;
const slugFromURL = _trim(pathname, '/');
// Available themes
const availableThemes = {
  themeOpac,
  themeCpmClient,
  themeCpmManager,
};
// Charge the theme based on the product
const computeThemeName = (PRODUCT) => {
  switch (PRODUCT) {
    case mProduct.getCpmManagerValue:
      return 'themeCpmManager';
    case mProduct.getCpmClientValue:
      return 'themeCpmClient';
    default:
      return 'themeOpac';
  }
};

const AppCpm = ({
  children,
  layoutBoxed,
  navCollapsed,
  navBehind,
  fixedHeader,
  sidebarWidth,
  theme,
  slug,
  handleGetSlugByDomain,
}) => {
  // Create material UI theme
  const muiTheme = createMuiTheme(availableThemes[theme]);

  useEffect(() => {
    // Fetch the slug id
    handleGetSlugByDomain(domainFromURL, slugFromURL);
  }, []);

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <MuiThemeProvider theme={muiTheme}>
        <MuiThemeProvider theme={(outerTheme) => _merge(main, outerTheme)}>
          <CssBaseline />
          <React.Fragment>
            <ResponseInterceptor />
            <ErrorBoundary>
              <div
                className={classnames('w-100', {
                  'fixed-header': fixedHeader,
                  'nav-collapsed': navCollapsed,
                  'nav-behind': navBehind,
                  'layout-boxed': layoutBoxed,
                  'theme-gray': theme === 'gray',
                  'theme-dark': theme === 'dark',
                  'sidebar-sm': sidebarWidth === 'small',
                  'sidebar-lg': sidebarWidth === 'large',
                })}
              >
                <LoadingDisplayer loading={(slug.loading || !slug.result) && !slug.error}>
                  {!slug.error ? children : <Error404 />}
                </LoadingDisplayer>
              </div>
            </ErrorBoundary>
            <DialogsManager />
          </React.Fragment>
        </MuiThemeProvider>
      </MuiThemeProvider>
    </MuiPickersUtilsProvider>
  );
};

AppCpm.propTypes = {
  children: PropTypes.element.isRequired,
  layoutBoxed: PropTypes.bool.isRequired,
  navCollapsed: PropTypes.bool.isRequired,
  navBehind: PropTypes.bool.isRequired,
  fixedHeader: PropTypes.bool.isRequired,
  sidebarWidth: PropTypes.string.isRequired,
  theme: PropTypes.string.isRequired,
  slug: cpmCurrentSlugShape.isRequired,
  handleGetSlugByDomain: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  layoutBoxed: state.settings.layoutBoxed,
  navCollapsed: state.settings.navCollapsed,
  navBehind: state.settings.navBehind,
  fixedHeader: state.settings.fixedHeader,
  sidebarWidth: state.settings.sidebarWidth,
  theme: _get(state.settings, computeThemeName(__PRODUCT__)), // eslint-disable-line no-undef
  slug: state.cpmSlug.current,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  handleGetSlugByDomain: (domain, slug) => {
    dispatch(
      cpmGetSlugByDomain(queGetSlugByDomain(ownProps.hasPermission), {
        domain,
        slug,
      }),
    );
  },
});

module.exports = compose(withPermissions, connect(mapStateToProps, mapDispatchToProps))(AppCpm);



// WEBPACK FOOTER //
// ./containers/AppCpm.js